import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

// ---- Cost telemetry ------------------------------------------------------

export interface CostTelemetryRow {
  id: string;
  recorded_at: string;
  service: string;
  metric: string;
  current_usage: number;
  free_tier_limit: number;
  unit: string;
  period: string;
}

export function useLatestCostTelemetry() {
  return useQuery({
    queryKey: ['cost-telemetry-latest'],
    queryFn: async (): Promise<CostTelemetryRow[]> => {
      const { data, error } = await supabase
        .from('cost_telemetry_latest')
        .select('*')
        .order('service', { ascending: true });
      if (error) throw error;
      return (data ?? []) as CostTelemetryRow[];
    },
  });
}

// ---- MFA enrollment ------------------------------------------------------
//
// We rely on Supabase Auth's TOTP factor machinery for the actual
// challenge — `supabase.auth.mfa.enroll`, `.challenge`, `.verify`.
// Our admin_mfa_state table is a thin mirror so RLS policies + the
// admin nav can answer "is this admin enrolled?".

export interface AdminMfaState {
  user_id: string;
  enrolled_at: string | null;
  recovery_codes_generated_at: string | null;
  last_aal2_at: string | null;
  last_email_mfa_at: string | null;
}

export function useMyMfaState() {
  return useQuery({
    queryKey: ['admin-mfa-state'],
    queryFn: async (): Promise<AdminMfaState | null> => {
      const { data, error } = await supabase.from('admin_mfa_state').select('*').maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

export interface MfaEnrollResult {
  factorId: string;
  qr: string; // SVG data URL
  secret: string;
  uri: string;
}

export function useMfaEnroll() {
  return useMutation({
    mutationFn: async (): Promise<MfaEnrollResult> => {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Pirouette admin',
      });
      if (error) throw error;
      const t = data.totp;
      return { factorId: data.id, qr: t.qr_code, secret: t.secret, uri: t.uri };
    },
  });
}

export function useMfaVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { factorId: string; code: string }) => {
      const challenge = await supabase.auth.mfa.challenge({ factorId: args.factorId });
      if (challenge.error) throw challenge.error;
      const verify = await supabase.auth.mfa.verify({
        factorId: args.factorId,
        challengeId: challenge.data.id,
        code: args.code,
      });
      if (verify.error) throw verify.error;
      // Now we should be aal2. Persist the enrolled state.
      const { error: markErr } = await supabase.rpc('mark_admin_mfa_enrolled');
      if (markErr) throw markErr;
      return verify.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-mfa-state'] });
    },
  });
}

/**
 * Issues a fresh challenge for a previously-enrolled factor (for the
 * step-up-on-aal2-route flow). Returns the challenge id so the UI can
 * collect the code and call `verify`.
 */
export function useMfaChallenge() {
  return useMutation({
    mutationFn: async (args: { factorId: string; code: string }) => {
      const challenge = await supabase.auth.mfa.challenge({ factorId: args.factorId });
      if (challenge.error) throw challenge.error;
      const verify = await supabase.auth.mfa.verify({
        factorId: args.factorId,
        challengeId: challenge.data.id,
        code: args.code,
      });
      if (verify.error) throw verify.error;
      // Touch last_aal2_at so the admin UI knows we're current.
      await supabase.rpc('touch_admin_aal2');
      return verify.data;
    },
  });
}

export function useListMfaFactors() {
  return useQuery({
    queryKey: ['mfa-factors'],
    queryFn: async () => {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;
      return data;
    },
  });
}

export interface AdminEmailMfaStartResult {
  ok: boolean;
  sent_to: string;
  expires_at: string;
}

function throwFunctionError(error: unknown): never {
  if (error instanceof Error) throw error;
  throw new Error(typeof error === 'string' ? error : 'Edge Function request failed');
}

export function useRequestAdminEmailMfa() {
  return useMutation({
    mutationFn: async (): Promise<AdminEmailMfaStartResult> => {
      const result = (await supabase.functions.invoke<AdminEmailMfaStartResult>(
        'admin-email-mfa-start',
      )) as { data: AdminEmailMfaStartResult | null; error: unknown };
      if (result.error) throwFunctionError(result.error);
      return result.data!;
    },
  });
}

export interface AdminEmailMfaVerifyResult {
  ok: boolean;
  verified_at?: string;
  expires_at?: string;
}

export function useVerifyAdminEmailMfa() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string): Promise<boolean> => {
      const result = (await supabase.functions.invoke<AdminEmailMfaVerifyResult>(
        'admin-email-mfa-verify',
        {
          body: { code },
        },
      )) as { data: AdminEmailMfaVerifyResult | null; error: unknown };
      if (result.error) throwFunctionError(result.error);
      return result.data?.ok === true;
    },
    onSuccess: (ok) => {
      if (ok) {
        void qc.invalidateQueries({ queryKey: ['admin-mfa-state'] });
        void qc.invalidateQueries({ queryKey: ['audit-log'] });
        void qc.invalidateQueries({ queryKey: ['audit-chain-checks'] });
        void qc.invalidateQueries({ queryKey: ['aal'] });
      }
    },
  });
}

// ---- Audit log -----------------------------------------------------------

export interface AuditLogRow {
  id: string;
  occurred_at: string;
  actor_user_id: string | null;
  actor_role: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  before_state: unknown;
  after_state: unknown;
  prev_hash: string | null;
  row_hash: string;
}

export interface AuditLogFilters {
  resourceType?: string | undefined;
  action?: string | undefined;
  actorRole?: string | undefined;
  since?: string | undefined;
}

export function useAuditLog(filters: AuditLogFilters) {
  return useQuery({
    queryKey: ['audit-log', filters],
    queryFn: async (): Promise<AuditLogRow[]> => {
      let q = supabase
        .from('audit_log')
        .select('*')
        .order('occurred_at', { ascending: false })
        .limit(500);
      if (filters.resourceType) q = q.eq('resource_type', filters.resourceType);
      if (filters.action) q = q.like('action', `%${filters.action}%`);
      if (filters.actorRole)
        q = q.eq('actor_role', filters.actorRole as 'admin' | 'faculty' | 'parent' | 'dancer');
      if (filters.since) q = q.gte('occurred_at', filters.since);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export interface ChainVerification {
  rows_checked: number;
  first_bad_row_id: string | null;
  is_ok: boolean;
}

export function useVerifyAuditChain() {
  return useMutation({
    mutationFn: async (): Promise<ChainVerification> => {
      const { data, error } = await supabase.rpc('verify_audit_chain');
      if (error) throw error;
      return (data as ChainVerification[])[0]!;
    },
  });
}

export function useChainCheckHistory() {
  return useQuery({
    queryKey: ['audit-chain-checks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_chain_checks')
        .select('*')
        .order('ran_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      return data ?? [];
    },
  });
}

// ---- Backup-restore drills -----------------------------------------------

export interface BackupDrill {
  id: string;
  ran_at: string;
  source_backup_taken_at: string;
  target_environment: 'dev' | 'staging';
  restored_table_count: number;
  rows_restored: number;
  duration_seconds: number;
  result: 'success' | 'failure' | 'partial';
  notes: string | null;
}

export function useBackupDrills() {
  return useQuery({
    queryKey: ['backup-drills'],
    queryFn: async (): Promise<BackupDrill[]> => {
      const { data, error } = await supabase
        .from('backup_restore_drills')
        .select('*')
        .order('ran_at', { ascending: false })
        .limit(30);
      if (error) throw error;
      return (data ?? []) as BackupDrill[];
    },
  });
}

export function useRecordBackupDrill() {
  const qc = useQueryClient();
  return useMutation({
    // HIGH-5 (Sprint 10 review): initiated_by is filled from the DB
    // default = jwt_user_id() (set in 0021). The client doesn't supply it.
    mutationFn: async (args: Omit<BackupDrill, 'id' | 'ran_at'>) => {
      const { error } = await supabase.from('backup_restore_drills').insert(args);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['backup-drills'] });
    },
  });
}
